/**
 * Portal picker component for multi-account users.
 *
 * Displayed when the login response contains multiPortal: true.
 * Shows a card for each available account type; clicking a card
 * calls POST /api/v1/auth/select-portal and redirects on success.
 *
 * Mobile-first responsive: single column on xs, two columns on md+.
 * All interactive elements meet 48x48 CSS pixel minimum touch target (WCAG 2.5.8).
 * Keyboard accessible: focusable cards, Enter/Space to select (radio group pattern).
 *
 * @module shared/auth/PortalPicker
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { selectPortal, AuthApiError } from './auth-api';

/** Maps AccountType values to user-facing display metadata. */
const PORTAL_DISPLAY_MAP: Record<string, { name: string; description: string; icon: string }> = {
  Subscriber: {
    name: 'Subscriber Portal',
    description: 'Manage your health plans and coverage',
    icon: '\u{1F464}',
  },
  CarrierAdmin: {
    name: 'Carrier Administration',
    description: 'Manage carrier plans and enrollments',
    icon: '\u{1F3E2}',
  },
  CarrierUser: {
    name: 'Carrier Portal',
    description: 'View carrier operations and reports',
    icon: '\u{1F4CB}',
  },
  CarrierSupport: {
    name: 'Carrier Support',
    description: 'Handle subscriber support requests',
    icon: '\u{1F3A7}',
  },
  BrokerAdmin: {
    name: 'Broker Administration',
    description: 'Manage broker operations and clients',
    icon: '\u{1F4BC}',
  },
  BrokerUser: {
    name: 'Broker Portal',
    description: 'Access broker tools and client referrals',
    icon: '\u{1F91D}',
  },
  AssociationAdmin: {
    name: 'Association Administration',
    description: 'Manage association members and plans',
    icon: '\u{1F465}',
  },
  AssociationUser: {
    name: 'Association Portal',
    description: 'View association membership details',
    icon: '\u{1F4C4}',
  },
  AppAdmin: {
    name: 'Platform Administration',
    description: 'Manage platform settings and users',
    icon: '\u{2699}\u{FE0F}',
  },
  AppToolUser: {
    name: 'Platform Tools',
    description: 'Access internal platform tools',
    icon: '\u{1F527}',
  },
};

/** Maps AccountType to the portal redirect URL. */
export const PORTAL_URL_MAP: Record<string, string> = {
  Subscriber: '/subscriber-portal/dashboard',
  CarrierAdmin: '/carrier-admin-portal/dashboard',
  CarrierUser: '/carrier-admin-portal/dashboard',
  CarrierSupport: '/carrier-admin-portal/dashboard',
  BrokerAdmin: '/broker-admin-portal/dashboard',
  BrokerUser: '/broker-admin-portal/dashboard',
  AssociationAdmin: '/association-admin-portal/dashboard',
  AssociationUser: '/association-admin-portal/dashboard',
  AppAdmin: '/app-admin-portal/dashboard',
  AppToolUser: '/app-admin-portal/dashboard',
};

/** Set of account types recognised by the platform — used to filter server responses (Story 2336). */
const KNOWN_ACCOUNT_TYPES = new Set(Object.keys(PORTAL_DISPLAY_MAP));

/** Props for the PortalPicker component. */
export interface PortalPickerProps {
  /** Account types available for selection (from login response). */
  availableTypes: string[];
  /** Short-lived selection token from the login response (60s lifetime). */
  selectionToken: string;
  /** Called when selection token expires — parent should redirect to login. */
  onTokenExpired: () => void;
  /** ISO 8601 UTC timestamp when the selection token expires (from server). */
  expiresAt?: string;
}

/** Inline styles (mobile-first, no external CSS framework). */
const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '1rem',
    backgroundColor: '#f3f4f6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  } as React.CSSProperties,
  container: {
    width: '100%',
    maxWidth: '36rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    margin: '0 0 0.25rem 0',
    color: '#111827',
  } as React.CSSProperties,
  subtitle: {
    fontSize: '0.875rem',
    margin: '0 0 1.5rem 0',
    color: '#6b7280',
  } as React.CSSProperties,
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.75rem',
    marginBottom: '1rem',
  } as React.CSSProperties,
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    minHeight: '72px',
    padding: '1rem',
    backgroundColor: '#ffffff',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  cardSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  } as React.CSSProperties,
  cardDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  } as React.CSSProperties,
  cardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#f3f4f6',
    fontSize: '1.5rem',
    flexShrink: 0,
  } as React.CSSProperties,
  cardContent: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  cardName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  } as React.CSSProperties,
  cardDescription: {
    fontSize: '0.8125rem',
    color: '#6b7280',
    margin: '0.125rem 0 0 0',
  } as React.CSSProperties,
  error: {
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    color: '#991b1b',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
  } as React.CSSProperties,
  expiredMessage: {
    padding: '1rem',
    fontSize: '0.875rem',
    color: '#92400e',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  timer: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    margin: '0.75rem 0 0 0',
  } as React.CSSProperties,
  loadingOverlay: {
    position: 'fixed' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 50,
    fontSize: '1rem',
    color: '#374151',
  } as React.CSSProperties,
} as const;

/**
 * Portal picker component shown when login detects multiple active account grants.
 *
 * Uses the WAI-ARIA radio group pattern for accessible single-selection.
 * Cards are navigable with arrow keys, selectable with Enter/Space.
 */
export function PortalPicker({
  availableTypes,
  selectionToken,
  onTokenExpired,
  expiresAt,
}: PortalPickerProps): React.JSX.Element {
  // Story 2336: filter unknown account types before rendering
  const validTypes = useMemo(() => {
    const filtered = availableTypes.filter((type) => {
      if (!KNOWN_ACCOUNT_TYPES.has(type)) {
        console.warn(`[PortalPicker] Unknown account type received from server: "${type}". Excluded from portal selection.`);
        return false;
      }
      return true;
    });
    return filtered;
  }, [availableTypes]);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Story 2335: compute initial countdown from server expiresAt when available
  const initialTime = expiresAt
    ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000))
    : 60;

  const [expired, setExpired] = useState(initialTime <= 0 && !!expiresAt);
  const [secondsRemaining, setSecondsRemaining] = useState(initialTime);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const onTokenExpiredRef = useRef(onTokenExpired);

  useEffect(() => {
    onTokenExpiredRef.current = onTokenExpired;
  }, [onTokenExpired]);

  // Story 2336: if all types are unknown, trigger expiration immediately
  // Story 2335: if expiresAt is already past, trigger expiration on mount
  useEffect(() => {
    if (validTypes.length === 0 || (expiresAt && initialTime <= 0)) {
      setExpired(true);
      onTokenExpiredRef.current();
    }
  }, [validTypes, expiresAt, initialTime]);

  // Story 2335: countdown timer — recomputes from expiresAt each tick to avoid drift
  useEffect(() => {
    if (expired) return;

    if (expiresAt) {
      const interval = setInterval(() => {
        const remaining = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
        setSecondsRemaining(remaining > 0 ? remaining : 0);

        if (remaining <= 0) {
          clearInterval(interval);
          setExpired(true);
          onTokenExpiredRef.current();
        }
      }, 1000);
      return () => clearInterval(interval);
    }

    // Fallback: wall-clock based 60s countdown (backward compatibility)
    const endTime = Date.now() + 60 * 1000;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setSecondsRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        setExpired(true);
        onTokenExpiredRef.current();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleSelect = useCallback(
    async (accountType: string) => {
      if (loading || expired) return;

      setError('');
      setLoading(true);

      try {
        // The selectionToken serves as CSRF protection (unguessable, single-use, 60s lifetime)
        await selectPortal({ selectionToken, accountType });

        // Redirect to the appropriate portal
        const portalUrl = PORTAL_URL_MAP[accountType] ?? '/dashboard';
        window.location.href = portalUrl;
      } catch (err) {
        if (err instanceof AuthApiError) {
          if (err.status === 401) {
            setExpired(true);
            onTokenExpiredRef.current();
            return;
          }
          setError(err.message);
        } else {
          setError('Something went wrong. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [selectionToken, loading, expired],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          e.preventDefault();
          const next = (index + 1) % validTypes.length;
          setSelectedIndex(next);
          cardRefs.current[next]?.focus();
          break;
        }
        case 'ArrowUp':
        case 'ArrowLeft': {
          e.preventDefault();
          const prev = (index - 1 + validTypes.length) % validTypes.length;
          setSelectedIndex(prev);
          cardRefs.current[prev]?.focus();
          break;
        }
        case ' ':
        case 'Enter':
          e.preventDefault();
          void handleSelect(validTypes[index]);
          break;
        default:
          break;
      }
    },
    [validTypes, handleSelect],
  );

  if (expired) {
    return (
      <div style={styles.wrapper} data-testid="portal-picker">
        <div style={styles.container}>
          <div
            role="alert"
            style={styles.expiredMessage}
            data-testid="portal-picker-expired"
          >
            Session expired, please login again
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper} data-testid="portal-picker">
      <style>{`
        @media (min-width: 768px) {
          [data-testid="portal-picker-grid"] {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1rem !important;
          }
          [data-testid="portal-picker"] [data-testid="portal-picker-container"] {
            max-width: 42rem;
          }
        }
        [data-testid="portal-card"]:hover:not([aria-disabled="true"]) {
          border-color: #93c5fd;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        [data-testid="portal-card"]:focus-visible {
          outline: 3px solid #2563eb;
          outline-offset: 2px;
        }
        [data-testid="portal-card"]:focus:not(:focus-visible) {
          outline: none;
        }
      `}</style>

      {loading && (
        <div style={styles.loadingOverlay} data-testid="portal-picker-loading">
          Loading...
        </div>
      )}

      <div style={styles.container} data-testid="portal-picker-container">
        <h1 style={styles.title}>Choose Your Portal</h1>
        <p style={styles.subtitle}>Select which portal you would like to access</p>

        {error && (
          <div
            role="alert"
            style={styles.error}
            data-testid="portal-picker-error"
          >
            {error}
          </div>
        )}

        <div
          role="radiogroup"
          aria-label="Available portals"
          style={styles.grid}
          data-testid="portal-picker-grid"
        >
          {validTypes.map((type, index) => {
            const portal = PORTAL_DISPLAY_MAP[type];
            const isSelected = index === selectedIndex;

            return (
              <div
                key={type}
                ref={(el) => { cardRefs.current[index] = el; }}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={loading}
                aria-label={portal.name}
                tabIndex={isSelected ? 0 : -1}
                data-testid="portal-card"
                data-account-type={type}
                style={{
                  ...styles.card,
                  ...(isSelected ? styles.cardSelected : {}),
                  ...(loading ? styles.cardDisabled : {}),
                }}
                onClick={() => void handleSelect(type)}
                onKeyDown={(e) => handleKeyDown(e, index)}
              >
                <div style={styles.cardIcon} aria-hidden="true">
                  {portal.icon}
                </div>
                <div style={styles.cardContent}>
                  <p style={styles.cardName}>{portal.name}</p>
                  <p style={styles.cardDescription}>{portal.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        <p style={styles.timer} aria-hidden="true" data-testid="portal-picker-timer">
          Selection expires in {secondsRemaining}s
        </p>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
        >
          {secondsRemaining <= 10
            ? `${secondsRemaining} ${secondsRemaining === 1 ? 'second' : 'seconds'} remaining`
            : ''}
        </div>
      </div>
    </div>
  );
}
